import { useEffect, useState } from 'react';
import {
  makeStyles,
  tokens,
  Title1,
  Text,
  Spinner,
  Badge,
  Button,
  Input,
} from '@fluentui/react-components';
import {
  simulateRouting,
  REQUEST_CLASSES,
  type RoutingSimulation,
  type RoutingRequestClass,
  type RoutingCandidate,
} from '../api/routing';
import { providerBadgeColor, providerLabel } from '../lib/chatModelId';

// Workbench MVP Session C — Model Routing Simulator.
// Routes over OBSERVED per-model evidence (MatrixResults), not a static price
// sheet — the platform's "decisions on data, not vibes" thesis made executable.
// Mirrors Governance's card + table shell so the Workbench reads as one surface.

const useStyles = makeStyles({
  header: { marginBottom: '18px' },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px',
    alignItems: 'flex-end',
    marginBottom: '18px',
  },
  field: { display: 'flex', flexDirection: 'column' as const, gap: '4px' },
  fieldLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  classRow: { display: 'flex', gap: '6px', flexWrap: 'wrap' as const },
  narrow: { width: '120px' },
  hero: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '10px',
    padding: '18px 20px',
    marginBottom: '18px',
  },
  heroChosen: { border: `1px solid ${tokens.colorPaletteGreenBorderActive}` },
  heroTitleRow: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' },
  heroModel: { fontSize: '22px', fontWeight: 600 },
  weights: {
    display: 'flex',
    gap: '14px',
    marginTop: '10px',
    color: tokens.colorNeutralForeground2,
    fontSize: '13px',
    fontVariantNumeric: 'tabular-nums' as const,
  },
  tableWrap: { overflowX: 'auto' as const },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '13px',
  },
  th: {
    textAlign: 'left' as const,
    padding: '8px 10px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
    whiteSpace: 'nowrap' as const,
  },
  num: { textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' as const },
  td: {
    padding: '8px 10px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  chosenRow: { background: tokens.colorPaletteGreenBackground1 },
  ineligibleRow: { opacity: 0.55 },
});

const CLASS_IDS = REQUEST_CLASSES.map((c) => c.id);

function fmtUsd(v: number): string {
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 4 });
}

function reasonLabel(reason: string | null): string {
  switch (reason) {
    case 'insufficient-evidence': return 'insufficient evidence';
    case 'over-cost-budget':      return 'over cost budget';
    case 'over-latency-budget':   return 'over latency budget';
    default:                      return reason ?? '';
  }
}

function statusText(sim: RoutingSimulation): string {
  if (sim.status === 'ok') return 'Routed';
  if (sim.status === 'no_feasible_candidate') return 'No feasible model';
  return 'No evidence yet';
}

export default function RoutingSimulator() {
  const s = useStyles();
  const [requestClass, setRequestClass] = useState<RoutingRequestClass>('balanced');
  const [maxCost, setMaxCost] = useState('');
  const [maxLatency, setMaxLatency] = useState('');
  const [sim, setSim] = useState<RoutingSimulation | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setErr(null);
    simulateRouting({
      requestClass,
      maxCostUsd: parseFloat(maxCost) || 0,
      maxLatencyMs: parseFloat(maxLatency) || 0,
      windowDays: 7,
    })
      .then(setSim)
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [requestClass, maxCost, maxLatency]);

  const chosen = sim?.candidates.find((c) => c.isChosen) ?? null;

  return (
    <div data-testid="routing-page">
      <div className={s.header}>
        <Title1 as="h1">Model Routing Simulator</Title1>
        <Text as="p" size={300}>
          A synthetic "which model should serve this request?" decision, ranked over the models the
          platform has actually measured in the last {sim?.windowDays ?? 7} days. No provider is
          called — it routes on observed cost, latency and quality from your matrix runs.
        </Text>
      </div>

      <div className={s.toolbar}>
        <div className={s.field}>
          <span className={s.fieldLabel}>Request class</span>
          <div className={s.classRow}>
            {CLASS_IDS.map((id) => (
              <Button
                key={id}
                appearance={requestClass === id ? 'primary' : 'secondary'}
                size="small"
                onClick={() => setRequestClass(id)}
                data-testid={`routing-class-${id}`}
              >
                {REQUEST_CLASSES.find((c) => c.id === id)!.label}
              </Button>
            ))}
          </div>
        </div>
        <div className={s.field}>
          <span className={s.fieldLabel}>Max cost / req (USD)</span>
          <Input
            className={s.narrow}
            type="number"
            step="0.001"
            min="0"
            placeholder="no limit"
            value={maxCost}
            onChange={(_, d) => setMaxCost(d.value)}
            data-testid="routing-max-cost"
          />
        </div>
        <div className={s.field}>
          <span className={s.fieldLabel}>Max latency (ms)</span>
          <Input
            className={s.narrow}
            type="number"
            step="100"
            min="0"
            placeholder="no limit"
            value={maxLatency}
            onChange={(_, d) => setMaxLatency(d.value)}
            data-testid="routing-max-latency"
          />
        </div>
      </div>

      {loading && <Spinner label="Simulating routing decision…" />}
      {err && (
        <Text as="p" style={{ color: tokens.colorPaletteRedForeground1 }}>
          {err}
        </Text>
      )}

      {sim && !loading && (
        <>
          <div
            className={`${s.hero} ${sim.status === 'ok' ? s.heroChosen : ''}`}
            data-testid="routing-hero"
          >
            <div className={s.heroTitleRow}>
              <Badge
                appearance="filled"
                color={sim.status === 'ok' ? 'success' : sim.status === 'no_data' ? 'informative' : 'warning'}
              >
                {statusText(sim)}
              </Badge>
              {chosen && (
                <Badge appearance="tint" color={providerBadgeColor(chosen.providerId)}>
                  {providerLabel(chosen.providerId)}
                </Badge>
              )}
            </div>
            <div className={s.heroModel} data-testid="routing-chosen">
              {chosen ? chosen.displayLabel : '—'}
            </div>
            <Text as="p" size={300}>{sim.rationale}</Text>
            <div className={s.weights}>
              <span>Weights →</span>
              <span>quality {sim.weights.quality.toFixed(2)}</span>
              <span>cost {sim.weights.cost.toFixed(2)}</span>
              <span>latency {sim.weights.latency.toFixed(2)}</span>
              <span>· min samples {sim.minSamples}</span>
            </div>
          </div>

          <div className={s.tableWrap}>
            <table className={s.table} data-testid="routing-candidates">
              <thead>
                <tr>
                  <th className={s.th}>#</th>
                  <th className={s.th}>Model</th>
                  <th className={s.th}>Provider</th>
                  <th className={`${s.th} ${s.num}`}>Quality</th>
                  <th className={`${s.th} ${s.num}`}>Cost/req</th>
                  <th className={`${s.th} ${s.num}`}>Avg latency</th>
                  <th className={`${s.th} ${s.num}`}>P95 latency</th>
                  <th className={`${s.th} ${s.num}`}>Samples</th>
                  <th className={`${s.th} ${s.num}`}>Score</th>
                  <th className={s.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {sim.candidates.map((c: RoutingCandidate) => (
                  <tr
                    key={c.chatModelId}
                    className={c.isChosen ? s.chosenRow : c.eligible ? '' : s.ineligibleRow}
                    data-testid={`routing-row-${c.chatModelId}`}
                  >
                    <td className={s.td}>{c.rank ?? '—'}</td>
                    <td className={s.td}>{c.displayLabel}</td>
                    <td className={s.td}>{providerLabel(c.providerId)}</td>
                    <td className={`${s.td} ${s.num}`}>{c.avgQualityScore.toFixed(2)}</td>
                    <td className={`${s.td} ${s.num}`}>{fmtUsd(c.avgCostUsd)}</td>
                    <td className={`${s.td} ${s.num}`}>{c.avgLatencyMs.toFixed(0)}ms</td>
                    <td className={`${s.td} ${s.num}`}>{c.p95LatencyMs.toFixed(0)}ms</td>
                    <td className={`${s.td} ${s.num}`}>{c.sampleCount}</td>
                    <td className={`${s.td} ${s.num}`}>{c.score !== null ? c.score.toFixed(2) : '—'}</td>
                    <td className={s.td}>
                      {c.eligible ? (
                        <Badge appearance="tint" color="success">eligible</Badge>
                      ) : (
                        <Badge appearance="outline" color="warning">{reasonLabel(c.ineligibleReason)}</Badge>
                      )}
                    </td>
                  </tr>
                ))}
                {sim.candidates.length === 0 && (
                  <tr>
                    <td className={s.td} colSpan={10}>
                      <Text size={200}>No measured models in-window. Run a matrix benchmark to build routing evidence.</Text>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
